import React, { useEffect, useState } from "react";
import {
  getUsers,
  removeUser,
  editUser,
  changePasswordAdmin,
  assignAward,
  revokeAward,
  setUserIsActive,
} from "../../api/user.api.js";
import EditUserModal from "../../components/modals/editUser.modal.js";
import { useAuth } from "../../context/auth.context.js";
import { Button, Rating, Stack, Switch, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid } from "@mui/x-data-grid";
import ChangePasswordModal from "../../components/modals/changePassword.modal.js";
import { useSnackbar } from "../../context/snackbar.context.js";
import AssignAwardModal from "../../components/modals/assignAward.modal.js";
import RevokeAwardModal from "../../components/modals/revokeAward.modal.js";

const AdminUsersPage = () => {
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openPwModal, setOpenPwModal] = useState(false);
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [openRevokeModal, setOpenRevokeModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [refreshUsers, setRefreshUsers] = useState(false);
  const [userToEdit, setUserToEdit] = useState();
  const auth = useAuth();
  const showSnackbar = useSnackbar();

  useEffect(() => {
    getUsers().then((response) => {
      setUsers(response.data);
    });
  }, [refreshUsers]);

  const handleRemove = (id) => {
    removeUser(id)
      .then(() => {
        showSnackbar("User deleted!", "success");
        setRefreshUsers((prev) => !prev);
      })
      .catch(() => showSnackbar("Failed to remove User!", "error"));
  };

  const handleEditSubmit = (formData) => {
    editUser(formData)
      .then(() => {
        showSnackbar("User edited!", "success");
        setRefreshUsers((prev) => !prev);
      })
      .catch(() => showSnackbar("Failed edit User!", "error"));
  };

  const handlePwSubmit = (formData) => {
    changePasswordAdmin(userToEdit.id, formData.newPassword)
      .then(() => {
        setRefreshUsers((prev) => !prev);
        showSnackbar("Password changed!", "success");
      })
      .catch(() => showSnackbar("Could not change password!", "error"));
  };

  const handleAssignAwardSubmit = (formData) => {
    assignAward(userToEdit.id, formData.id)
      .then(() => {
        setRefreshUsers((prev) => !prev);
        showSnackbar("Award assigned!", "success");
      })
      .catch(() => showSnackbar("Could not assign award!", "error"));
  };

  const handleRevokeAwardSubmit = (formData) => {
    revokeAward(userToEdit.id, formData.id)
      .then(() => {
        setRefreshUsers((prev) => !prev);
        showSnackbar("Award revoked!", "success");
      })
      .catch(() => showSnackbar("Could not revoke award!", "error"));
  };

  const handleCloseModal = () => {
    setOpenEditModal(false);
    setOpenPwModal(false);
    setOpenAssignModal(false);
    setOpenRevokeModal(false);
    setUserToEdit();
  };

  const handleEdit = (user) => {
    setUserToEdit(user);
    setOpenEditModal(true);
  };

  const handleChangePassword = (user) => {
    setUserToEdit(user);
    setOpenPwModal(true);
  };

  const handleAssignAward = (user) => {
    setUserToEdit(user);
    setOpenAssignModal(true);
  };

  const handleRevokeAward = (user) => {
    setUserToEdit(user);
    setOpenRevokeModal(true);
  };

  const toggleIsActive = (userId, isActive) => {
    setUserIsActive(userId, !isActive)
      .then(() => setRefreshUsers((prev) => !prev))
      .catch((error) => console.log(error));
  };

  const columns = [
    {
      field: "nickname",
      headerName: "User",
      width: 150,
    },
    {
      field: "fullName",
      headerName: "Fullname",
      width: 200,
    },
    {
      field: "email",
      headerName: "Email",
      width: 250,
    },
    {
      field: "role",
      headerName: "Role",
      width: 100,
    },
    {
      field: "skillLevels",
      headerName: "Skill Levels",
      width: 200,
      height: 200,
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <div>
          {params.value.map((level) => (
            <div
              key={level.category}
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography variant="caption" style={{ flex: 1 }}>
                {level.category}
              </Typography>
              <Rating
                size="small"
                readOnly
                name={level.category}
                value={level.level}
              />
            </div>
          ))}
        </div>
      ),
    },
    {
      field: "isActive",
      headerName: "Active",
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <div>
          {auth && auth.user.id !== params.row.id && (
            <Switch
              size="small"
              checked={params.row.isActive}
              onChange={() =>
                toggleIsActive(params.row.id, params.row.isActive)
              }
              inputProps={{ "aria-label": "controlled" }}
            />
          )}
        </div>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 700,
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <Stack
          display="flex"
          spacing={1}
          direction="row"
          alignItems="center"
          justifyContent="center"
        >
          <Button
            variant="outlined"
            onClick={() => handleAssignAward(params.row)}
          >
            Assign Award
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleRevokeAward(params.row)}
          >
            Revoke Award
          </Button>
          {auth.user.id !== params.row.id && (
            <>
              <Button variant="outlined" onClick={() => handleEdit(params.row)}>
                Edit
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleChangePassword(params.row)}
              >
                Change Password
              </Button>

              <Button
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={() => handleRemove(params.row.id)}
              >
                Delete
              </Button>
            </>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <div>
      <Typography variant="h3" gutterBottom>
        User Administration
      </Typography>
      <DataGrid
        rows={users}
        columns={columns}
        sx={{ border: 0 }}
        disableSelectionOnClick
        disableColumnSelector
        getRowHeight={() => "auto"}
      />

      <EditUserModal
        aria-modal
        open={openEditModal}
        handleClose={handleCloseModal}
        handleSubmit={handleEditSubmit}
        userData={userToEdit}
      />

      <ChangePasswordModal
        aria-modal
        open={openPwModal}
        handleClose={handleCloseModal}
        handleSubmit={handlePwSubmit}
        user={userToEdit}
      />

      <AssignAwardModal
        aria-modal
        open={openAssignModal}
        handleClose={handleCloseModal}
        handleSubmit={handleAssignAwardSubmit}
      />

      <RevokeAwardModal
        aria-modal
        open={openRevokeModal}
        handleClose={handleCloseModal}
        handleSubmit={handleRevokeAwardSubmit}
        user={userToEdit}
      />
    </div>
  );
};

export default AdminUsersPage;
